import cx from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { useAtom } from 'jotai';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Chevrelon from '../../icons/cheverlon';

import classes from './image-group.module.css';
import { isSliderOpen, setTargetItem } from './state';
import Image from 'next/image';
import BPImage from '../../v2/image';

const ImageSlider: FunctionComponent<{
  list: any;
  specificWidth?: number;
  specificHeight?: number;
  cardView?: string;
  isPP?: boolean;
  altExtraInfo?: string;
}> = ({
  list,
  specificWidth,
  specificHeight,
  cardView,
  isPP = false,
  altExtraInfo,
}) => {
    const [, setShouldGetImages] = useState(false);
    const [page] = useState(0);
    const ref = useRef<HTMLElement>(null);
    const [emblaRef, embla] = useEmblaCarousel({ loop: true });

    useEffect(() => {
      const watcher: IntersectionObserverCallback = (entries) => {
        if (entries[0].intersectionRatio !== 0) {
          setShouldGetImages(true);
        }
      };

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      };

      let observer: IntersectionObserver;

      if (typeof IntersectionObserver !== 'undefined') {
        observer = new IntersectionObserver(watcher, options);

        ref.current && observer.observe(ref.current);
      }

      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }, []);

    useEffect(() => {
      if (embla) {
        embla.reInit(); // Garante que o carousel seja inicializado corretamente
      }
    }, [embla]);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    // Atualiza o estado dos botões quando o Embla é inicializado
    const updateButtons = useCallback(() => {
      if (!embla) return;
      setCanScrollPrev(embla.canScrollPrev());
      setCanScrollNext(embla.canScrollNext());
    }, [embla]);

    useEffect(() => {
      if (!embla) return;
      embla.on('select', updateButtons);
      updateButtons();
    }, [embla, updateButtons]);

    const [open, setOpen] = useAtom(isSliderOpen);
    const [, setTarget] = useAtom(setTargetItem);

    return (
      <div
        className={cx(
          'embla embla__viewport h-full transform transition-transform translate-x-0 relative overflow-hidden',
          cardView ? `w-full` : 'w-[100vw]'
        )}
        ref={emblaRef}
      >
        <div
          className={cx(
            'embla__container flex flex-row',
            isPP
              ? 'aspect-video w-full h-fit'
              : specificHeight
                ? `!h-[${specificHeight.toFixed()}px]`
                : 'h-full'
          )}
        >
          {list.map((item, index) => {
            return (
              <div
                className={cx(
                  'embla__slide flex h-full cursor-pointer relative',
                  cardView
                    ? `flex-[0_0_100%] min-w-full`
                    : 'flex-[0_0_100vw] min-w-[100vw]'
                )}
                key={index}
                onClick={(e) => {
                  //e.preventDefault();
                  setTarget(index), setOpen(true);
                }}
              >
                <div
                  className={cx(
                    'relative w-full',
                    isPP ? 'pb-[56.25%]' : '', // .imageRatio
                    !isPP && 'pb-[67%]' // .imageRatioSmall para mobile
                  )}
                >{(() => {
                  const imageElement = (
                    <img
                      className={cx(
                        'absolute inset-0 w-full h-full object-cover',
                        isPP
                          ? 'w-full h-fit aspect-video'
                          : specificWidth
                            ? `w-[${specificWidth.toFixed()}px]`
                            : '',
                        isPP
                          ? ''
                          : specificHeight
                            ? `h-[${specificHeight.toFixed()}px]`
                            : cardView
                              ? cardView === 'COLUMN'
                                ? 'h-[184px]'
                                : cardView === 'PP'
                                  ? 'h-[219px] lg:h-[411px]'
                                  : 'h-[200px]'
                              : 'h-[219px]'
                      )}
                      src={item.src_big || item.image_big}
                      alt={`${altExtraInfo ? altExtraInfo + ' ' : ''}`}
                      fetchPriority={index === 0 ? 'high' : 'low'}
                    />);

                  return imageElement;
                })()}
                </div>
              </div>
            );
          })}
        </div>
        <button
          className={cx(
            'embla__button embla__button--prev',
            'absolute h-8 w-8 z-10 left-2 top-[calc(50%-16px)]'
          )}
          onClick={(e) => {
            e.preventDefault();
            embla && embla.scrollPrev();
          }}
          disabled={!canScrollPrev}
          aria-label="previous slide"
        >
          <Chevrelon
            className={cx('text-neutral-10 rotate-90 ', classes.shadowLeft)}
          ></Chevrelon>
        </button>
        <button
          className={cx(
            'embla__button embla__button--next disabled:opacity-50 disabled:cursor-not-allowed',
            'absolute h-8 w-8 right-2 z-10 top-[calc(50%-16px)]'
          )}
          onClick={(e) => {
            e.preventDefault();
            embla && embla.scrollNext();
          }}
          disabled={!canScrollNext}
          aria-label="next slide"
        >
          <Chevrelon
            className={cx('text-neutral-10 -rotate-90 ', classes.shadowRight)}
          ></Chevrelon>
        </button>
      </div>
    );
  };

export default ImageSlider;
